import React from "react";
import PropTypes from "prop-types";
import { clx } from "../../../utils/Helpers";
import UIAvatar from "../UIAvatar";
import UIStatusTag from "../UIStatusTag";
import { getStatusTagConfig } from "../UIStatusTag/statusTagStyles";
import "./_UIPageHeader.scss";

const renderSubtitle = (subtitle, classNames) => {
  if (subtitle == null || subtitle === false || subtitle === "") return null;

  const items = Array.isArray(subtitle) ? subtitle.filter(Boolean) : [subtitle];

  return (
    <div className={clx("ui-page-header__subtitle", classNames.subtitle)}>
      {items.map((item, index) => (
        <React.Fragment key={`subtitle-${index}`}>
          {index > 0 ? (
            <span
              aria-hidden="true"
              className={clx(
                "ui-page-header__subtitle-dot",
                classNames.subtitleDivider
              )}
            />
          ) : null}
          <span
            className={clx(
              "ui-page-header__subtitle-item",
              classNames.subtitleItem
            )}
          >
            {item}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const UIPageHeader = ({
  actions,
  avatar,
  avatarSize = "lg",
  badges,
  children,
  className = "",
  classNames = {},
  icon,
  iconBackground,
  iconStatus,
  name = "",
  status,
  statusLabel,
  statusPreset = "header",
  statusProps = {},
  statusTone,
  subtitle,
  title,
  ...props
}) => {
  const imageSrc = avatar?.src || avatar?.image;
  const avatarName =
    typeof avatar?.name === "string"
      ? avatar.name
      : typeof name === "string"
        ? name
        : "";
  const shouldRenderAvatar = Boolean(avatar || imageSrc || avatarName);
  const shouldRenderStatus = Boolean(status);
  const iconConfig = getStatusTagConfig({
    status: iconStatus || status,
    preset: "header",
    tone: statusTone,
  });
  const iconStyle = {
    "--page-header-icon-bg": iconBackground || iconConfig.style.background,
  };

  return (
    <section className={clx("ui-page-header", className, classNames.root)} {...props}>
      <div className={clx("ui-page-header__inner", classNames.inner)}>
        <div className={clx("ui-page-header__main", classNames.main)}>
          {shouldRenderAvatar ? (
            <UIAvatar
              {...avatar}
              className={clx("ui-page-header__avatar", avatar?.className, classNames.avatar)}
              name={avatarName}
              size={avatar?.size || avatarSize}
            />
          ) : icon ? (
            <div
              className={clx("ui-page-header__icon", classNames.icon)}
              style={iconStyle}
            >
              {icon}
            </div>
          ) : null}

          <div className={clx("ui-page-header__content", classNames.content)}>
            <div className={clx("ui-page-header__title-row", classNames.titleRow)}>
              <h1 className={clx("ui-page-header__title", classNames.title)}>
                {title}
              </h1>
              {shouldRenderStatus ? (
                <UIStatusTag
                  className={clx("ui-page-header__status", classNames.status)}
                  label={statusLabel}
                  preset={statusPreset}
                  status={status}
                  tone={statusTone}
                  {...statusProps}
                />
              ) : null}
              {badges}
            </div>
            {renderSubtitle(subtitle, classNames)}
            {children ? (
              <div className={clx("ui-page-header__extra", classNames.extra)}>
                {children}
              </div>
            ) : null}
          </div>
        </div>

        {actions ? (
          <div className={clx("ui-page-header__actions", classNames.actions)}>
            {actions}
          </div>
        ) : null}
      </div>
    </section>
  );
};

UIPageHeader.propTypes = {
  actions: PropTypes.node,
  avatar: PropTypes.shape({
    alt: PropTypes.string,
    className: PropTypes.string,
    image: PropTypes.string,
    initials: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
    src: PropTypes.string,
  }),
  avatarSize: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  badges: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  classNames: PropTypes.shape({
    actions: PropTypes.string,
    avatar: PropTypes.string,
    content: PropTypes.string,
    extra: PropTypes.string,
    icon: PropTypes.string,
    inner: PropTypes.string,
    main: PropTypes.string,
    root: PropTypes.string,
    status: PropTypes.string,
    subtitle: PropTypes.string,
    subtitleDivider: PropTypes.string,
    subtitleItem: PropTypes.string,
    title: PropTypes.string,
    titleRow: PropTypes.string,
  }),
  icon: PropTypes.node,
  iconBackground: PropTypes.string,
  iconStatus: PropTypes.string,
  name: PropTypes.string,
  status: PropTypes.string,
  statusLabel: PropTypes.node,
  statusPreset: PropTypes.oneOf(["table", "header"]),
  statusProps: PropTypes.object,
  statusTone: PropTypes.string,
  subtitle: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  title: PropTypes.node.isRequired,
};

UIPageHeader.defaultProps = {
  actions: null,
  avatar: null,
  avatarSize: "lg",
  badges: null,
  children: null,
  className: "",
  classNames: {},
  icon: null,
  iconBackground: undefined,
  iconStatus: undefined,
  name: "",
  status: undefined,
  statusLabel: undefined,
  statusPreset: "header",
  statusProps: {},
  statusTone: undefined,
  subtitle: null,
};

export default UIPageHeader;
